import { useEffect, useState } from "react";
import { Check, ListChecks, Loader2, LockKeyhole } from "lucide-react";
import api from "@/lib/api";
import logo from "@/assets/logo.png";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { BackButton } from "@/components/BackButton";

const SharedSection = ({ section, index }) => (
  <section data-testid={`shared-packing-section-${index}`} className="border-t border-stone-100 pt-6">
    <h2 data-testid={`shared-packing-section-title-${index}`} className="font-['Nunito'] font-bold text-base md:text-lg text-ac-softfg break-words">{section.title}</h2>
    {section.items.length ? <ul className="mt-3 space-y-3">
      {section.items.map((item, itemIndex) => (
        <li key={item.id} data-testid={`shared-packing-item-${index}-${itemIndex}`} className="flex items-start gap-3 text-sm sm:text-base">
          <span aria-hidden="true" className={`mt-0.5 w-5 h-5 border rounded-md shrink-0 grid place-items-center ${item.packed ? "border-ac bg-ac-soft text-ac" : "border-stone-300 bg-white"}`}>
            {item.packed && <Check className="w-3.5 h-3.5" strokeWidth={3} />}
          </span>
          <span className={`min-w-0 break-words ${item.packed ? "line-through text-stone-400" : "text-stone-700"}`}>{item.text}<span className="sr-only">{item.packed ? " (packed)" : " (not packed)"}</span></span>
        </li>
      ))}
    </ul> : <p data-testid={`shared-packing-section-empty-${index}`} className="mt-2 text-sm text-stone-400">No items in this section yet.</p>}
  </section>
);

export default function SharedPackingList({ token }) {
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    const robots = document.createElement("meta");
    robots.name = "robots";
    robots.content = "noindex, nofollow, noarchive";
    document.head.appendChild(robots);
    const referrer = document.createElement("meta");
    referrer.name = "referrer";
    referrer.content = "no-referrer";
    document.head.appendChild(referrer);
    return () => { robots.remove(); referrer.remove(); };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setList(null);
    setError("");
    api.get(`/shared/packing/${encodeURIComponent(token)}`, { signal: controller.signal, withCredentials: false })
      .then(({ data }) => setList(data))
      .catch((err) => {
        if (!controller.signal.aborted) setError(err.response?.status === 404
          ? "This sharing link isn't available. The list may have been deleted, or the link is incorrect."
          : "We couldn't load this packing list. Please try again.");
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [token, retry]);

  const items = list?.sections.flatMap((section) => section.items) || [];
  const done = items.filter((item) => item.packed).length;
  const pct = items.length ? Math.round(done / items.length * 100) : 0;

  return (
    <main data-testid="shared-packing-page" className="min-h-screen bg-stone-50 px-5 py-8 sm:py-12 font-['Figtree']">
      <div className="max-w-2xl mx-auto min-w-0">
        <BackButton className="-ml-4 mb-3" />
        <img src={logo} alt="pikyourtrip" className="h-12 max-w-full object-contain object-left mb-8" />
        {loading ? <p data-testid="shared-packing-loading" role="status" className="flex items-center gap-2 text-stone-500"><Loader2 className="h-5 w-5 animate-spin" /> Loading packing list…</p>
          : error ? <div className="rounded-3xl border border-stone-200 bg-white p-6 space-y-4">
            <h1 className="font-['Nunito'] text-2xl font-bold">List unavailable</h1>
            <p data-testid="shared-packing-error" role="alert" className="text-sm text-stone-500">{error}</p>
            <Button data-testid="shared-packing-retry" onClick={() => setRetry((value) => value + 1)} variant="outline" className="rounded-full font-bold">Try again</Button>
          </div>
          : list && <article className="rounded-3xl border border-stone-200 bg-white p-5 sm:p-8 shadow-sm space-y-6">
            <header className="min-w-0">
              <p data-testid="shared-packing-read-only" className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-ac-softfg"><LockKeyhole className="w-3.5 h-3.5" /> Shared list · Read only</p>
              <h1 data-testid="shared-packing-title" className="font-['Nunito'] text-4xl sm:text-5xl font-bold text-stone-900 break-words mt-3">{list.title}</h1>
              <p data-testid="shared-packing-info" className="text-sm text-stone-500 mt-3">A little preparation for the next adventure. Only the owner can make changes.</p>
              <div className="flex items-center gap-3 mt-5">
                <ListChecks className="w-5 h-5 text-ac shrink-0" />
                <Progress data-testid="shared-packing-progress" value={pct} aria-label="Packing progress" className="h-2 flex-1" />
                <span data-testid="shared-packing-count" className="text-xs font-bold text-stone-500 whitespace-nowrap">{done}/{items.length} packed</span>
              </div>
            </header>
            {list.sections.map((section, index) => <SharedSection key={section.id} section={section} index={index} />)}
            {!list.sections.length && <p data-testid="shared-packing-empty" className="text-sm text-stone-500">No sections yet.</p>}
          </article>}
      </div>
    </main>
  );
}
